import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

/**
 * An app-wide side drawer, reserved for later developer tooling (docs/design/ops-tooling-options.md:
 * the date override, debug bundles, a plan inspector). Nothing opens it yet. A future tool calls
 * `useAppDrawer().open(<Tool />, "Title")`; the drawer renders over every page, signed in or not.
 */
type DrawerState = { title: string; content: ReactNode } | null;
type DrawerApi = { open: (content: ReactNode, title?: string) => void; close: () => void };

const DrawerContext = createContext<DrawerApi | null>(null);

export function AppDrawerProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DrawerState>(null);
  const open = useCallback((content: ReactNode, title = "") => setState({ title, content }), []);
  const close = useCallback(() => setState(null), []);
  const api = useMemo(() => ({ open, close }), [open, close]);
  return (
    <DrawerContext.Provider value={api}>
      {children}
      {state && (
        <aside className="wp-drawer" role="dialog" aria-label={state.title || "Tools"}>
          <div className="wp-drawer-head">
            <b>{state.title}</b>
            <button type="button" onClick={close} aria-label="Close">
              &times;
            </button>
          </div>
          <div className="wp-drawer-body">{state.content}</div>
        </aside>
      )}
    </DrawerContext.Provider>
  );
}

export function useAppDrawer(): DrawerApi {
  const ctx = useContext(DrawerContext);
  if (!ctx) throw new Error("useAppDrawer must be used inside <AppDrawerProvider>");
  return ctx;
}
